import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, catchError, map, of, switchMap } from 'rxjs';

import { C_value } from '@app/shared/_models/common';
import { todayStr } from '@app/shared/time';
import type { Day, ReasonUsage, RentalLifecycleFull, RentalLineView, RentalSaleLineView } from './rental-billing.model';
import { RentalBillingService, serverMessage } from './rental-billing.service';
import { addDays, minDay } from './rental-days';

const COLOR = /^#[0-9a-f]{6}$/i;

// Vue du cycle d'un dossier, partagée par la page B2B, le Planning et les dialogues ; fourni par l'hôte, jamais à la racine.
@Injectable()
export class RentalLifecycleStore {
  private svc = inject(RentalBillingService);
  private destroyRef = inject(DestroyRef);

  orderId = 0;
  view: RentalLifecycleFull | null = null; // jamais remis à null pour un même dossier
  firstLoad = false; // premier chargement en cours : aucune vue de ce dossier n'est encore arrivée
  refreshing = false; // rechargement en cours, l'ancienne vue reste affichée
  error: string | null = null; // dernier échec ; n'efface pas la vue
  until: Day | null = null; // DERNIER jour de la frise demandé au serveur, inclus
  reasons: C_value[] = [];
  reasonsState: 'loading' | 'ok' | 'missing' | 'error' = 'loading';
  beneficiary: string | null = null; // « nom prénom » du bénéficiaire (data.recipient, sinon le client) : patient des lignes sans mémo
  revision = 0; // change avec la vue, les motifs, les noms ou le bénéficiaire ; pas au simple début d'un rechargement (refreshing)

  private changed = new Subject<void>();
  private saved = new Subject<number>();
  private attached = new Subject<void>();
  readonly changed$: Observable<void> = this.changed.asObservable();
  readonly saved$: Observable<number> = this.saved.asObservable();
  readonly attached$: Observable<void> = this.attached.asObservable(); // fichier d'ordonnance joint (après l'enregistrement de la décision)

  private requests = new Subject<{ orderId: number; until: Day | null; open: boolean } | null>();
  // Vrai : vue des seules lignes en place (matériels repris non lus) ; loadClosed() relit tout.
  openOnly = true;
  private users = new Map<number, string>();
  private periods = new Map<number, string>();
  private refsLoaded = false;

  constructor() {
    this.requests
      .pipe(
        switchMap((req) =>
          req === null
            ? of(null)
            : this.svc.getLifecycle(req.orderId, req.until, req.open).pipe(
                map((view) => ({ req, view, error: null as string | null })),
                catchError((e) => of({ req, view: null as RentalLifecycleFull | null, error: serverMessage(e) as string | null })),
              ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((r) => {
        if (r === null || r.req.orderId !== this.orderId) return;
        this.firstLoad = false;
        this.refreshing = false;
        this.revision++;
        if (r.view) {
          this.view = r.view;
          this.error = null;
        } else {
          this.error = r.error;
        }
        this.changed.next();
      });
  }

  open(orderId: number): void {
    if (orderId === this.orderId) return;
    this.orderId = orderId;
    this.openOnly = true;
    this.revision++;
    this.view = null;
    this.beneficiary = null;
    this.error = null;
    this.refreshing = false;
    this.firstLoad = false;
    this.until = addDays(todayStr(), 91); // borne de requête seule (dernier jour inclus, avant : 92 exclu) : le jour affiché reste as_of du serveur
    this.requests.next(null); // abandonne la requête du dossier précédent
    this.loadRefs();
    this.reload();
  }

  reload(): void {
    if (this.orderId <= 0) return;
    this.firstLoad = this.view === null;
    this.refreshing = this.view !== null;
    this.changed.next();
    this.requests.next({ orderId: this.orderId, until: this.until, open: this.openOnly });
  }

  loadClosed(): void {
    this.openOnly = false;
    this.reload();
  }

  // day = dernier jour que l'écran doit voir, inclus ; la frise est alors prolongée d'un mois, sans dépasser deux ans après as_of (dernier jour inclus).
  ensureUntil(day: Day): void {
    if (this.orderId <= 0 || this.until === null || !(day > this.until)) return;
    const cap = addDays(this.view?.as_of ?? todayStr(), 729);
    const next = minDay(addDays(day, 31), cap) as Day;
    if (!(next > this.until)) return;
    this.until = next;
    this.reload();
  }

  filesAttached(): void {
    this.attached.next();
  }

  // Posé par qui a lu l'en-tête du dossier (page du dossier, Planning) ; les vues se refont si le nom change.
  setBeneficiary(orderId: number, name: string | null | undefined): void {
    const n = name?.trim() || null;
    if (orderId !== this.orderId || n === this.beneficiary) return;
    this.beneficiary = n;
    this.revision++;
    this.changed.next();
  }

  decisionsSaved(count: number): void {
    this.reload();
    this.saved.next(count);
  }

  line(id: number): RentalLineView | undefined {
    return this.view?.lines.find((l) => l.id === id);
  }

  sale(id: number): RentalSaleLineView | undefined {
    return this.view?.sales.find((s) => s.id === id);
  }

  rentalLines(): RentalLineView[] {
    return (this.view?.lines ?? []).filter((l) => l.typ === 'RENTAL').sort((a, b) => a.seq - b.seq);
  }

  reasonsFor(usage: ReasonUsage): C_value[] {
    return this.reasons.filter((r) => r.is_active === true && r.data?.usage === usage).sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  }

  reasonName(id: number): string {
    return this.reasons.find((r) => r.id === id)?.name || `motif ${id}`;
  }

  reasonColor(id: number): string | null {
    const c = this.reasons.find((r) => r.id === id)?.data?.color;
    return typeof c === 'string' && COLOR.test(c) ? c : null;
  }

  reasonNotice(id: number): string | null {
    const n = this.reasons.find((r) => r.id === id)?.data?.fse_notice;
    return typeof n === 'string' && n.trim() ? n.trim() : null;
  }

  reasonFlag(id: number, flag: 'from_delivery' | 'comment_required'): boolean {
    return this.reasons.find((r) => r.id === id)?.data?.[flag] === true;
  }

  // Motif d'interruption aux jours offerts (data.offered = true, motif 12 « Geste commercial ») : l'interruption se montre comme un geste ; absent = faux.
  reasonOffered(id: number): boolean {
    return this.reasons.find((r) => r.id === id)?.data?.offered === true;
  }

  userName(id: number): string {
    return this.users.get(id) ?? `utilisateur ${id}`;
  }

  periodName(id: number | null): string | null {
    return id == null ? null : (this.periods.get(id) ?? null);
  }

  // Motifs relus à l'ouverture d'un dialogue si leur lecture a échoué : sans eux, aucune décision ne se saisit.
  ensureReasons(): void {
    if (this.reasonsState === 'error') this.loadReasons();
  }

  // Motifs, utilisateurs et périodes : une fois par store, quel que soit le dossier.
  private loadRefs(): void {
    if (this.refsLoaded) return;
    this.refsLoaded = true;
    this.loadReasons();
    this.svc.getUserNames().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (m) => {
        this.users = m;
        this.revision++;
        this.changed.next();
      },
      error: () => {}, // l'auteur reste affiché par son identifiant
    });
    this.svc.getPeriodNames().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (m) => {
        this.periods = m;
        this.revision++;
        this.changed.next();
      },
      error: () => {},
    });
  }

  private loadReasons(): void {
    this.reasonsState = 'loading';
    this.svc.getReasons().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (list) => {
        this.reasons = list;
        this.reasonsState = list.length ? 'ok' : 'missing';
        this.revision++;
        this.changed.next();
      },
      error: () => {
        this.reasonsState = 'error';
        this.revision++;
        this.changed.next();
      },
    });
  }
}
